import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle, XCircle,
  DollarSign, Phone, Target, Wrench, BarChart3, Mail, ArrowRight,
  Star, Activity, Users, Clock, Zap, ChevronRight
} from "lucide-react";

interface BusinessHubData {
  businessHealth: {
    score: number;
    metrics: Array<{ name: string; score: number; change: number; }>;
  };
  customerService: {
    avgUxScore: number;
    maxScore: number;
    metrics: Array<{ name: string; value: string | number; change: number; }>;
  };
  salesConversions: {
    overallPercentage: number;
    subMetrics: Array<{ name: string; count: number; rate: number; costPer: number; }>;
  };
  financials: {
    revenue: { totalInvoiced: number; totalPaid: number; outstanding: number; };
    expenses: { total: number; };
    cashFlow: { bankBalance: number; };
    profitMargin: number;
  };
}

function ScoreGauge({ score, max = 1000, size = "lg" }: { score: number; max?: number; size?: "sm" | "lg" }) {
  const pct = (score / max) * 100;
  const r = size === "lg" ? 42 : 28;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const color = pct >= 70 ? "#22c55e" : pct >= 40 ? "#f59e0b" : "#ef4444";
  const label = pct >= 70 ? "Good" : pct >= 40 ? "Fair" : "Poor";
  const vb = size === "lg" ? "0 0 100 100" : "0 0 64 64";
  const cx = size === "lg" ? 50 : 32;
  const cy = size === "lg" ? 50 : 32;
  const sw = size === "lg" ? 8 : 5;
  const textSize = size === "lg" ? "text-3xl" : "text-xl";
  const w = size === "lg" ? "w-28 h-28" : "w-16 h-16";
  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`relative ${w}`}>
        <svg viewBox={vb} className="w-full h-full -rotate-90">
          <circle cx={cx} cy={cy} r={r} fill="none" strokeWidth={sw} className="text-muted/30" stroke="currentColor" />
          <circle cx={cx} cy={cy} r={r} fill="none" strokeWidth={sw}
            stroke={color} strokeLinecap="round"
            strokeDasharray={`${dash} ${circ}`} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`${textSize} font-bold leading-none`} style={{ color }}>{score}</span>
          {size === "lg" && <span className="text-xs text-muted-foreground">/{max}</span>}
        </div>
      </div>
      <span className="text-xs font-semibold" style={{ color }}>{label}</span>
    </div>
  );
}

function RAGBadge({ value, green, amber }: { value: number; green: number; amber: number }) {
  if (value >= green) return <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1" />;
  if (value >= amber) return <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1" />;
  return <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1" />;
}

function Trend({ val }: { val: number }) {
  if (val > 0) return <span className="text-green-400 text-xs flex items-center gap-0.5"><TrendingUp className="h-3 w-3" />+{val}%</span>;
  if (val < 0) return <span className="text-red-400 text-xs flex items-center gap-0.5"><TrendingDown className="h-3 w-3" />{val}%</span>;
  return <span className="text-muted-foreground text-xs flex items-center gap-0.5"><Minus className="h-3 w-3" />0%</span>;
}

export default function ProDashboard() {
  const { data: hubData, isLoading: hubLoading } = useQuery<BusinessHubData>({
    queryKey: ["/api/business-hub"],
  });
  const { data: reportData, isLoading: reportLoading } = useQuery<any>({
    queryKey: ["/api/report"],
  });

  const isLoading = hubLoading || reportLoading;

  const health = hubData?.businessHealth;
  const cs = hubData?.customerService;
  const sales = hubData?.salesConversions;
  const fin = hubData?.financials;
  const overall = reportData?.overall;
  const callMetrics = reportData?.callMetrics;
  const scheduleMetrics = reportData?.scheduleMetrics;

  const healthScore = health?.score ?? 520;
  const csScore = cs?.avgUxScore ?? 3.4;
  const salesPct = sales?.overallPercentage ?? 42;
  const revenue = fin?.revenue?.totalInvoiced ?? 124000;
  const outstanding = fin?.revenue?.outstanding ?? 18400;
  const profitMargin = fin?.profitMargin ?? 16.8;
  const bankBalance = fin?.cashFlow?.bankBalance ?? 287000;
  const totalCalls = callMetrics?.totalCalls ?? 631;
  const missedCalls = callMetrics?.missedCalls ?? 178;
  const activeEngineers = scheduleMetrics?.activeEngineers ?? 11;
  const totalJobs = scheduleMetrics?.totalJobs ?? 156;
  const leads = overall?.totalConversions ?? 47;
  const avgDeal = 4500;

  const reportingSuites = [
    { title: "Financial Hub", icon: DollarSign, href: "/financial-hub", color: "text-emerald-400",
      metrics: [
        { label: "Revenue", value: "£" + (revenue/1000).toFixed(0) + "K", trend: 15 },
        { label: "Margin", value: profitMargin.toFixed(1) + "%", trend: -8 },
        { label: "Cash", value: "£" + (bankBalance/1000).toFixed(0) + "K", trend: 12 },
        { label: "Outstanding", value: "£" + (outstanding/1000).toFixed(1) + "K", trend: -5 },
      ],
      alert: profitMargin < 20 ? "Margin below 20% target" : null,
      alertLevel: "amber" as const
    },
    { title: "Marketing Intelligence", icon: Target, href: "/marketing-intelligence", color: "text-purple-400",
      metrics: [
        { label: "Leads", value: String(leads), trend: 12 },
        { label: "ROAS", value: "4.2x", trend: -18 },
        { label: "Avg Deal", value: "£" + (avgDeal/1000).toFixed(1) + "K", trend: 8 },
        { label: "Conversion", value: salesPct.toFixed(0) + "%", trend: -5 },
      ],
      alert: "ROAS declining -18% MoM",
      alertLevel: "amber" as const
    },
    { title: "Operations Centre", icon: Wrench, href: "/operations-centre", color: "text-blue-400",
      metrics: [
        { label: "Jobs Done", value: String(totalJobs), trend: 8 },
        { label: "Engineers", value: String(activeEngineers), trend: 0 },
        { label: "Utilisation", value: "45%", trend: -15 },
        { label: "FTF Rate", value: "89%", trend: 1 },
      ],
      alert: "Engineer utilisation 45% — losing £18.4K/mo",
      alertLevel: "red" as const
    },
    { title: "Customer Service", icon: Phone, href: "/customer-service", color: "text-orange-400",
      metrics: [
        { label: "Total Calls", value: String(totalCalls), trend: 12 },
        { label: "Missed", value: String(missedCalls), trend: -28 },
        { label: "CSAT", value: csScore.toFixed(1) + "/5", trend: -6 },
        { label: "Resolution", value: "94%", trend: 2 },
      ],
      alert: missedCalls > 100 ? missedCalls + " missed calls = £34K lost revenue" : null,
      alertLevel: "red" as const
    },
    { title: "Report Distribution", icon: Mail, href: "/report-distribution", color: "text-sky-400",
      metrics: [
        { label: "Schedules", value: "6", trend: 0 },
        { label: "Recipients", value: "12", trend: 0 },
        { label: "Sent MTD", value: "142", trend: 0 },
        { label: "Last Run", value: "08:00", trend: 0 },
      ],
      alert: null,
      alertLevel: "green" as const
    },
    { title: "Executive Summary", icon: Star, href: "/executive-summary", color: "text-yellow-400",
      metrics: [
        { label: "Health", value: String(healthScore) + "/1K", trend: 0 },
        { label: "Revenue", value: "£124K", trend: 15 },
        { label: "Pipeline", value: "£438K", trend: 22 },
        { label: "EBITDA", value: "£34.2K", trend: -5 },
      ],
      alert: null,
      alertLevel: "amber" as const
    },
  ];

  const salesFunnel = sales?.subMetrics ?? [
    { name: "Clicks to Enquiry", count: 1250, rate: 68, costPer: 2.40 },
    { name: "Enquiry to Quote", count: 850, rate: 54, costPer: 12.50 },
    { name: "Quote to Sale", count: 320, rate: 38, costPer: 45.00 },
  ];

  const healthMetrics = health?.metrics ?? [
    { name: "Cash Flow", score: 72, change: -8 },
    { name: "Job Completion", score: 85, change: 2 },
    { name: "Customer Satisfaction", score: 58, change: -6 },
    { name: "Team Utilisation", score: 45, change: -15 },
    { name: "Revenue Growth", score: 78, change: 3 },
    { name: "Profit Margin", score: 34, change: -18 },
    { name: "Mktg Cost of Sale", score: 28, change: -22 },
    { name: "Sales Conversion", score: 52, change: -5 },
  ];

  const csMetrics = cs?.metrics ?? [
    { name: "Total Calls", value: 631, change: 12 },
    { name: "Inbound", value: 461, change: 8 },
    { name: "Outbound", value: 137, change: 0 },
    { name: "Missed Calls", value: 178, change: -28 },
    { name: "Avoidable Missed", value: 129, change: -72 },
    { name: "Avg Duration (s)", value: 87, change: 5 },
    { name: "Calls Today", value: 114, change: 15 },
    { name: "This Week", value: 629, change: 10 },
  ];

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-40 bg-muted/20 rounded-xl animate-pulse" />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-48 bg-muted/20 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6 text-primary" />
            Keystone Pro Dashboard
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Live high-level overview — all systems — February 2026
          </p>
        </div>
        <div className="flex gap-2">
          <Link href="/executive-summary">
            <Button variant="outline" size="sm" className="gap-1">
              <Star className="h-3.5 w-3.5" /> Executive View
            </Button>
          </Link>
          <Link href="/report-distribution">
            <Button variant="outline" size="sm" className="gap-1">
              <Mail className="h-3.5 w-3.5" /> Reports
            </Button>
          </Link>
        </div>
      </div>

      {/* TOP 3 HERO METRICS — Business Health, Customer Service, Sales Conversion */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Business Health Score */}
        <Card className="border-border/60 bg-card/80">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Business Health</p>
                <p className="text-xs text-muted-foreground mt-0.5">8 KPIs — Target: 750/1000</p>
              </div>
              <Link href="/executive-summary">
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-primary">
                  Full View <ChevronRight className="h-3 w-3" />
                </Button>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <ScoreGauge score={healthScore} max={1000} size="lg" />
              <div className="flex-1 grid grid-cols-2 gap-1.5">
                {healthMetrics.map((m) => {
                  const pct = m.score;
                  const color = pct >= 70 ? "text-green-400" : pct >= 40 ? "text-amber-400" : "text-red-400";
                  return (
                    <div key={m.name} className="flex items-center gap-1.5 text-xs">
                      <RAGBadge value={m.score} green={70} amber={40} />
                      <span className="text-muted-foreground truncate">{m.name}</span>
                      <span className={`ml-auto font-semibold ${color}`}>{m.score}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Customer Service Score */}
        <Card className="border-border/60 bg-card/80">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Customer Service</p>
                <p className="text-xs text-muted-foreground mt-0.5">Call Centre — CSAT Score</p>
              </div>
              <Link href="/customer-service">
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-primary">
                  Full View <ChevronRight className="h-3 w-3" />
                </Button>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <ScoreGauge score={Math.round(csScore * 20)} max={100} size="lg" />
              <div className="flex-1 space-y-1.5">
                {csMetrics.slice(0, 6).map((m) => (
                  <div key={m.name} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{m.name}</span>
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold">{typeof m.value === "number" ? m.value.toLocaleString() : m.value}</span>
                      <Trend val={m.change} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sales Conversions */}
        <Card className="border-border/60 bg-card/80">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Sales Conversions</p>
                <p className="text-xs text-muted-foreground mt-0.5">Full funnel — Click to Sale</p>
              </div>
              <Link href="/marketing-intelligence">
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-primary">
                  Full View <ChevronRight className="h-3 w-3" />
                </Button>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <ScoreGauge score={Math.round(salesPct)} max={100} size="lg" />
              <div className="flex-1 space-y-2">
                {salesFunnel.map((s: any, i: number) => {
                  const rate = s.rate ?? (s.percentage || 0);
                  const cost = s.costPer ?? (s.cost || 0);
                  const count = s.count ?? 0;
                  const w = (rate / 100) * 100;
                  const barColor = rate >= 60 ? "bg-green-500" : rate >= 40 ? "bg-amber-500" : "bg-red-500";
                  return (
                    <div key={i} className="space-y-0.5">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">{s.name}</span>
                        <span className="font-semibold">{rate}% — £{cost.toFixed(2)}</span>
                      </div>
                      <div className="h-1.5 bg-muted/30 rounded-full overflow-hidden">
                        <div className={`h-full ${barColor} rounded-full`} style={{ width: w + "%" }} />
                      </div>
                      <p className="text-xs text-muted-foreground">Count: {count.toLocaleString()}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reporting Suites Grid */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <BarChart3 className="h-4 w-4" /> Reporting Suites — Keystone Business Support Hub
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reportingSuites.map((suite) => {
            const Icon = suite.icon;
            const alertColors = {
              red: "border-red-500/40 bg-red-500/5",
              amber: "border-amber-500/40 bg-amber-500/5",
              green: "border-green-500/40 bg-green-500/5",
            };
            return (
              <Link key={suite.title} href={suite.href}>
                <Card className="border-border/60 bg-card/80 hover:border-primary/50 hover:bg-card transition-all cursor-pointer group">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-md bg-muted/30">
                          <Icon className={`h-4 w-4 ${suite.color}`} />
                        </div>
                        <span className="font-semibold text-sm">{suite.title}</span>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {suite.metrics.map((m) => (
                        <div key={m.label} className="space-y-0.5">
                          <p className="text-xs text-muted-foreground">{m.label}</p>
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-bold">{m.value}</span>
                            {m.trend !== 0 && <Trend val={m.trend} />}
                          </div>
                        </div>
                      ))}
                    </div>
                    {suite.alert && (
                      <div className={`rounded-md px-2 py-1.5 text-xs border ${alertColors[suite.alertLevel]} flex items-center gap-1.5`}>
                        <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                        <span>{suite.alert}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Bottom Row: Key Alerts + Quick KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* AI Critical Alerts */}
        <Card className="border-red-500/30 bg-red-500/5">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-4 w-4" /> Critical Actions Required
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-2">
            {[
              { icon: Wrench, text: "Engineer utilisation at 45% — below 85% target. Losing £18,400/month in unbilled capacity.", href: "/operations-centre", level: "red" },
              { icon: Phone, text: "178 missed calls this month = £34,000 estimated lost revenue. Implement overflow answering.", href: "/customer-service", level: "red" },
              { icon: DollarSign, text: "Net margin at 16.8% — below 20% target. COGS up 21% due to materials spike.", href: "/financial-hub", level: "amber" },
              { icon: Target, text: "ROAS declining -18% MoM. Google Ads CPE risen to £15.24. Review bidding strategy.", href: "/marketing-intelligence", level: "amber" },
            ].map((alert, i) => {
              const Icon = alert.icon;
              const bg = alert.level === "red" ? "bg-red-500/10 border-red-500/20" : "bg-amber-500/10 border-amber-500/20";
              const tc = alert.level === "red" ? "text-red-400" : "text-amber-400";
              return (
                <Link key={i} href={alert.href}>
                  <div className={`flex items-start gap-2 p-2 rounded border ${bg} cursor-pointer hover:opacity-80 transition-opacity`}>
                    <Icon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${tc}`} />
                    <span className="text-xs text-foreground/80">{alert.text}</span>
                  </div>
                </Link>
              );
            })}
          </CardContent>
        </Card>

        {/* Revenue & KPI Strip */}
        <Card className="border-border/60 bg-card/80">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Zap className="h-4 w-4 text-yellow-400" /> Key Performance Indicators — Feb 2026
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Monthly Revenue", value: "£124,000", trend: 15, sub: "Target £130K" },
                { label: "Net Profit", value: "£20,900", trend: -29, sub: "Margin 16.8%" },
                { label: "Cash Position", value: "£287,000", trend: 12, sub: "94 day runway" },
                { label: "Active Pipeline", value: "£438,000", trend: 22, sub: "£189K weighted" },
                { label: "Jobs Completed", value: "156", trend: 8, sub: "88.2% in progress" },
                { label: "Blended ROAS", value: "4.2x", trend: -18, sub: "£6.1K ad spend" },
                { label: "First Time Fix", value: "89%", trend: 1, sub: "Target 95%" },
                { label: "Outstanding Debt", value: "£18,400", trend: -5, sub: "18 days avg" },
              ].map((kpi) => (
                <div key={kpi.label} className="bg-muted/10 rounded-lg p-2.5 space-y-0.5">
                  <p className="text-xs text-muted-foreground">{kpi.label}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="text-base font-bold">{kpi.value}</span>
                    <Trend val={kpi.trend} />
                  </div>
                  <p className="text-xs text-muted-foreground/70">{kpi.sub}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

    </div>
  );
}